import {
  Component,
  Input,
  Output,
  EventEmitter,
  HostListener,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';

export interface Vehicle {
  id: string;
  name: string;
  licensePlate: string;
  vin: string;
  isFamilyPlanVehicle: boolean;
}

/**
 * A presentational card component for displaying vehicle information with optional selection controls, family-plan status and mobile action menu.
 * This component renders vehicle details and exposes actions for editing, deleting (via a confirmation modal), and selection changes. It also supports a compact/no-card visual option and a mobile-friendly overflow menu that is automatically closed on outside clicks.
 * @component
 * @selector app-vehicle-card
 * @example
 *   <app-vehicle-card
 *     [vehicle]="car"
 *     [selectable]="true"
 *     [selected]="false"
 *     selectionType="checkbox"
 *     (editVehicle)="onEdit($event)"
 *     (showDeleteModal)="confirmDelete($event)"
 *     (selectionChange)="onSelectionChange($event)">
 *   </app-vehicle-card>
 *
 * Inputs:
 * @input {Vehicle} vehicle - The vehicle object to display (id, name, licensePlate, vin, isFamilyPlanVehicle). Defaults to a sample Porsche object if not provided.
 * @input {boolean} isParentVehicleCard - Alias 'isparentvehiclecrad'. When true, show the selection control used for family-plan lists.
 * @input {boolean} selectable - When true, show selection control (radio/checkbox) inside the card.
 * @input {boolean} selected - Current selection state of the card.
 * @input {string} selectionName - Name attribute used for selection controls. Default: 'vehicleSelection'.
 * @input {'radio'|'checkbox'} selectionType - Type of selection control. Default: 'radio'.
 * @input {boolean} showFamilyPlanStatus - When true, render family plan status information. Default: true.
 * @input {boolean} noCardStyle - When true, render without the default card chrome (compact/no-card mode).
 * @input {boolean} showDeleteButton - When true, render a delete action button. Default: true.
 * @input {boolean} useMobileMenu - When true, enable mobile overflow menu for actions.
 * @input {boolean} disabled - When true, disable interactive controls in the card.
 *
 * Outputs:
 * @output {EventEmitter<Vehicle>} editVehicle - Emitted when the user requests to edit the vehicle (via primary or mobile action).
 * @output {EventEmitter<Vehicle>} deleteVehicle - Emitted to indicate a delete action (available if consumers subscribe or the component uses it).
 * @output {EventEmitter<Vehicle>} showDeleteModal - Emitted to request showing a delete confirmation modal for the vehicle (used by default delete flow).
 * @output {EventEmitter<boolean>} selectionChange - Emitted with the new checked state when the selection control changes.
 *
 * Key methods:
 * - onEdit(): Emits editVehicle with the current vehicle.
 * - onDelete(): Emits showDeleteModal with the current vehicle to request confirmation.
 * - onSelectionChange(event: Event): Reads the input checked state and emits selectionChange.
 * - toggleMobileMenu(event: Event): Toggles the internal mobile menu state and stops propagation.
 * - onMobileEdit(): Closes mobile menu and delegates to onEdit().
 * - onMobileDelete(): Closes mobile menu and delegates to onDelete().
 * - closeMobileMenu(): Explicitly closes the mobile menu.
 *
 * Other details:
 * - Detects touch devices via isTouch ('ontouchstart' in window || navigator.maxTouchPoints > 0) to inform UI behavior.
 * - showMobileMenu (boolean) tracks the mobile overflow menu visibility.
 * - Uses a HostListener on document:click to close the mobile menu when clicking outside.
 * - No explicit Angular lifecycle hooks (ngOnInit, ngOnDestroy, etc.) are implemented in this component.
 */
@Component({
  selector: 'app-vehicle-card',
  standalone: true,
  imports: [CommonModule, MatTooltipModule],
  templateUrl: './vehicle-card.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./vehicle-card.component.scss'],
})
export class VehicleCardComponent {
  @Input() vehicle: Vehicle = {
    id: '',
    name: '',
    licensePlate: '',
    vin: '',
    isFamilyPlanVehicle: false,
  };

  // When true, show the selection control inside the card (as per family-plan list)
  // Accept alias 'isparentvehiclecrad' to match consumer spelling
  @Input() isParentVehicleCard = false;
  @Input() selectable = false; // guard to show control
  @Input() selected = false;
  @Input() selectionName = 'vehicleSelection';
  @Input() selectionType: 'radio' | 'checkbox' = 'radio';
  @Input() showFamilyPlanStatus = true;
  @Input() noCardStyle = false;
  @Input() showDeleteButton = true;
  @Input() useMobileMenu = false; // New input to control mobile menu display
  @Input() disabled = false;

  @Output() editVehicle = new EventEmitter<Vehicle>();
  @Output() deleteVehicle = new EventEmitter<Vehicle>();
  @Output() showDeleteModal = new EventEmitter<Vehicle>();
  @Output() selectionChange = new EventEmitter<boolean>();

  // Touch device detection
  isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  // Mobile menu state
  showMobileMenu = false;

  onEdit() {
    this.editVehicle.emit(this.vehicle);
  }

  onDelete() {
    this.showDeleteModal.emit(this.vehicle);
  }

  onSelectionChange(event: Event) {
    const checked = (event.target as HTMLInputElement)?.checked ?? false;
    this.selectionChange.emit(checked);
  }

  // Mobile menu methods
  toggleMobileMenu(event: Event) {
    event.stopPropagation();
    this.showMobileMenu = !this.showMobileMenu;
  }

  onMobileEdit() {
    this.showMobileMenu = false;
    this.onEdit();
  }

  onMobileDelete() {
    this.showMobileMenu = false;
    this.onDelete();
  }

  closeMobileMenu() {
    this.showMobileMenu = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    if (this.showMobileMenu) {
      this.closeMobileMenu();
    }
  }
}
